/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { ArrowLeft } from "lucide-react";

const Para = ({ children }) => (
  <p className="mt-4 text-[15px] leading-7 text-[#A1A1AA]">{children}</p>
);

const Sub = ({ children }) => (
  <h3 className="mt-9 mb-1 font-mono text-xs uppercase tracking-[0.18em] text-white">
    {children}
  </h3>
);

const InShort = ({ children }) => (
  <p className="my-5 border-l-2 border-[#C9A94B] bg-[#0A0A0A] px-4 py-3 text-sm leading-6">
    <span className="font-bold text-[#C9A94B]">In Short: </span>
    <span className="text-[#C9A94B]/80">{children}</span>
  </p>
);

const Bullets = ({ items }) => (
  <ul className="my-4 space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-[15px] leading-7 text-[#A1A1AA]">
        <span className="mt-[11px] h-1 w-1 shrink-0 bg-[#C9A94B]" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const A = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-[#C9A94B] underline underline-offset-2 transition-colors hover:text-[#DCC271]"
  >
    {children}
  </a>
);

const Section = ({ n, title, children }) => (
  <section id={`sec-${n}`} className="mt-12 scroll-mt-28 border-t border-[#1F1F1F] pt-10">
    <div className="mb-4 flex items-baseline gap-4">
      <span className="font-mono text-sm text-[#C9A94B]">{String(n).padStart(2, "0")}</span>
      <h2 className="font-display text-3xl uppercase leading-none tracking-tight text-white md:text-4xl">
        {title}
      </h2>
    </div>
    {children}
  </section>
);

const TOC_ITEMS = [
  "What information do we collect?",
  "How do we process your information?",
  "What legal bases do we rely on to process your information?",
  "When and with whom do we share your personal information?",
  "Is your information transferred internationally?",
  "How long do we keep your information?",
  "How do we keep your information safe?",
  "Do we collect information from minors?",
  "What are your privacy rights?",
  "Controls for do-not-track features",
  "Do United States residents have specific privacy rights?",
  "Do we make updates to this notice?",
  "How can you contact us about this notice?",
  "How can you review, update, or delete the data we collect from you?",
];

const CATEGORY_ROWS = [
  ["A", "Identifiers", "Contact details, such as real name, alias, postal address, phone number, unique personal identifier, online identifier, IP address, email address, and account name", "YES"],
  ["B", "California Customer Records", "Name, contact information, education, employment, employment history, and financial information", "NO"],
  ["C", "Protected classifications", "Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data", "NO"],
  ["D", "Commercial information", "Transaction information, purchase history, financial details, and payment information", "YES"],
  ["E", "Biometric information", "Fingerprints and voiceprints", "NO"],
  ["F", "Internet or network activity", "Browsing history, search history, online behavior, interest data, and interactions with our systems and ads", "NO"],
  ["G", "Geolocation data", "Device location", "NO"],
  ["H", "Audio, electronic, sensory", "Images you submit as proof, including rear-camera task photos and front-camera selfie images", "YES"],
  ["I", "Professional / employment", "Business contact details, job title, work history, and professional qualifications", "NO"],
  ["J", "Education information", "Student records and directory information", "NO"],
  ["K", "Inferences", "Inferences drawn from collected personal information to create a profile or summary", "NO"],
  ["L", "Sensitive personal information", "Account login information and the contents of direct messages or comments", "YES"],
];

export default function Privacy() {
  const lenis = useLenis();

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [lenis]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { offset: -90 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="privacy-page" className="grain min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b border-[#1F1F1F] bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 md:px-8">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A] transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={2} />
            Back to home
          </Link>
          <span className="text-xl font-bold lowercase tracking-tight text-white">proof</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pb-28 pt-14 md:px-8">
        {/* Title */}
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C9A94B]">
          Legal · prooftous.app
        </span>
        <h1 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
          Privacy Policy
        </h1>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.15em] text-[#8A8A8A]">
          Last updated August 01, 2026
        </p>

        <Para>
          This Privacy Notice for Proof It ("we," "us," or "our") describes how and why we might
          access, collect, store, use, and/or share ("process") your personal information when you
          use our services ("Services"), including when you:
        </Para>
        <Bullets
          items={[
            "Download and use our mobile application (Proof It), or any other application of ours that links to this Privacy Notice.",
            "Use Proof It — a social accountability app where users declare a daily commitment and then prove they completed it by capturing a dual-camera photo (a rear photo of the task plus a front-camera selfie). Proofs appear in a feed visible to the user's connections, who can like, react with emoji, comment, and send direct messages. Users build streaks and a personal record. A paid Pro subscription unlocks additional features.",
            "Engage with us in other related ways, including any marketing or events.",
          ]}
        />
        <Para>
          Questions or concerns? Reading this Privacy Notice will help you understand your privacy
          rights and choices. We are responsible for making decisions about how your personal
          information is processed. If you do not agree with our policies and practices, please do
          not use our Services. If you still have any questions or concerns, please contact us at{" "}
          <A href="mailto:hello@prooftous.app">hello@prooftous.app</A>.
        </Para>

        {/* Summary */}
        <section className="mt-12 border-t border-[#1F1F1F] pt-10">
          <h2 className="font-display text-3xl uppercase leading-none tracking-tight text-white md:text-4xl">
            Summary of Key Points
          </h2>
          <Para>
            This summary provides key points from our Privacy Notice, but you can find more details
            about any of these topics using the table of contents below.
          </Para>
          <Bullets
            items={[
              "What personal information do we process? When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.",
              "Do we process any sensitive personal information? Yes. When you submit proof, we process the photographs you capture, including your front-camera selfie image. Depending on your jurisdiction, this may be considered 'sensitive' personal information. We process it only with your consent or as otherwise permitted by applicable law.",
              "Do we collect any information from third parties? We do not purchase or collect personal information about you from data brokers or other outside sources. We do receive limited purchase and subscription-status information from Apple and RevenueCat when you buy a subscription, solely to activate and manage your Pro access.",
              "How do we process your information? We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law — only when we have a valid legal reason to do so.",
              "In what situations and with which parties do we share personal information? We may share information in specific situations and with specific third parties, including your chosen connections within the app and the service providers who support our Services.",
              "How do we keep your information safe? We have organizational and technical processes and procedures in place to protect your personal information. However, no method of transmission or storage can be guaranteed to be 100% secure.",
              "What are your rights? Depending on where you are located, applicable privacy law may give you certain rights regarding your personal information.",
              "How do you exercise your rights? The easiest way is by emailing us at hello@prooftous.app or by using your in-app account settings.",
            ]}
          />
        </section>

        {/* Table of contents */}
        <section className="mt-12 border-t border-[#1F1F1F] pt-10">
          <h2 className="font-display text-3xl uppercase leading-none tracking-tight text-white md:text-4xl">
            Table of Contents
          </h2>
          <ol className="mt-6 space-y-2">
            {TOC_ITEMS.map((t, i) => (
              <li key={i}>
                <button
                  onClick={() => go(`sec-${i + 1}`)}
                  className="group flex w-full items-baseline gap-3 text-left transition-colors"
                >
                  <span className="font-mono text-xs text-[#C9A94B]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[15px] text-[#A1A1AA] underline-offset-4 group-hover:text-white group-hover:underline">
                    {t}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </section>

        {/* Sections */}
        <Section n={1} title="What Information Do We Collect?">
          <Sub>Personal information you disclose to us</Sub>
          <InShort>We collect personal information that you provide to us.</InShort>
          <Para>
            We collect personal information that you voluntarily provide to us when you register for
            the Services, express an interest in obtaining information about us or our products and
            Services, participate in activities on the Services, or otherwise contact us.
          </Para>
          <Para>
            <span className="text-white">Personal Information Provided by You.</span> The personal
            information we collect depends on the context of your interactions with us and the
            Services, the choices you make, and the features you use. It may include:
          </Para>
          <Bullets
            items={[
              "Email addresses",
              "Usernames",
              "Passwords",
              "Profile information (such as a display name and profile photo)",
              "Content you create, including commitments, comments, reactions, and direct messages",
            ]}
          />
          <Para>
            <span className="text-white">Sensitive Information.</span> When necessary, with your
            consent or as otherwise permitted by applicable law, we process the following categories
            of sensitive information:
          </Para>
          <Bullets
            items={[
              "Photographs and images you submit as proof, including rear-camera task photos and front-camera selfie images.",
            ]}
          />
          <Para>
            <span className="text-white">Payment Data.</span> When you purchase a Proof Pro
            subscription, the transaction is processed through the Apple App Store. Your payment card
            details are collected and stored directly by Apple — we never receive or store your full
            payment card information. We use RevenueCat as a subscription-management intermediary to
            validate purchase receipts and manage your subscription status and entitlements. You may
            review their privacy notices here:{" "}
            <A href="https://www.apple.com/legal/privacy/">Apple</A> ·{" "}
            <A href="https://www.revenuecat.com/privacy/">RevenueCat</A>.
          </Para>
          <Para>
            <span className="text-white">Application Data.</span> If you use our application(s), we
            may also process the following if you grant the relevant permission:
          </Para>
          <Bullets
            items={[
              "Camera. We access your device camera so you can capture the rear-camera and front-camera images that make up your proof.",
              "Push Notifications. We may request to send you push notifications regarding your account or certain features, including reminders about commitments, streaks, and social activity. You can turn them off in your device settings.",
            ]}
          />
          <Para>
            This information is primarily needed to maintain the security and operation of our
            application(s), for troubleshooting, and for our internal analytics and reporting
            purposes. All personal information you provide to us must be true, complete, and
            accurate, and you must notify us of any changes to such personal information.
          </Para>
        </Section>

        <Section n={2} title="How Do We Process Your Information?">
          <InShort>
            We process your information to provide, improve, and administer our Services, communicate
            with you, for security and fraud prevention, and to comply with law.
          </InShort>
          <Bullets
            items={[
              "To facilitate account creation and authentication and otherwise manage user accounts.",
              "To deliver and facilitate delivery of services, including capturing, storing, and displaying your proofs and streaks.",
              "To capture, store, and display user-submitted photos (rear-camera and front-camera selfie images) as part of the accountability/proof feature, and to share them with the connections you choose.",
              "To enable user-to-user communications, including likes, reactions, comments, and direct messages.",
              "To send push notifications and reminders related to commitments, streaks, and social activity.",
              "To respond to user inquiries, offer support, and resolve issues with the Services.",
              "To send administrative information, such as changes to our terms and policies.",
              "To manage subscriptions, including validating purchases and activating or deactivating Pro features.",
              "To save or protect an individual's vital interests, such as to prevent harm.",
            ]}
          />
        </Section>

        <Section n={3} title="What Legal Bases Do We Rely On?">
          <InShort>
            We only process your personal information when we believe it is necessary and we have a
            valid legal reason to do so under applicable law.
          </InShort>
          <Sub>If you are located in the EU or UK</Sub>
          <Para>
            The GDPR and UK GDPR require us to explain the valid legal bases we rely on. We may rely
            on the following:
          </Para>
          <Bullets
            items={[
              "Consent. We may process your information if you have given us permission to use it for a specific purpose — for example, to capture and share your proof photos. You can withdraw your consent at any time.",
              "Performance of a Contract. To fulfill our contractual obligations to you, including providing our Services.",
              "Legal Obligations. Where necessary for compliance with our legal obligations, such as cooperating with law enforcement or defending our legal rights.",
              "Vital Interests. Where necessary to protect your vital interests or those of a third party.",
            ]}
          />
          <Sub>If you are located in Canada</Sub>
          <Para>
            We may process your information if you have given express consent, or where your
            permission can be inferred (implied consent). You can withdraw your consent at any time.
            In some exceptional cases, we may be legally permitted to process your information without
            your consent, including for investigations and fraud detection and prevention, certain
            business transactions, and where disclosure is required to comply with a subpoena,
            warrant, or court order. We may disclose de-identified information for approved research
            or statistics projects, subject to ethics oversight and confidentiality commitments.
          </Para>
        </Section>

        <Section n={4} title="When And With Whom Do We Share?">
          <InShort>
            We may share information in specific situations described below and/or with the following
            third parties.
          </InShort>
          <Para>
            <span className="text-white">Vendors, Consultants, and Service Providers.</span> We may
            share your data with third-party vendors, service providers, contractors, or agents who
            perform services for us and require access to do that work. We have contracts in place
            designed to safeguard your personal information; they cannot use it for anything other
            than what we instruct. The categories of third parties are:
          </Para>
          <Bullets
            items={[
              "Cloud Computing & Infrastructure: Google Cloud Platform, Firebase (including Firebase Cloud Messaging for push notifications)",
              "Payment & Subscription Processing: Apple App Store, RevenueCat",
              "App Testing & Distribution: Apple TestFlight",
            ]}
          />
          <Para>We also may need to share your personal information in the following situations:</Para>
          <Bullets
            items={[
              "Business Transfers. In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business.",
              "Other Users. When you post proofs, comments, reactions, or other content, that information may be viewed by the connections you have chosen and, depending on your settings, by other users, who can also view your profile and activity.",
            ]}
          />
        </Section>

        <Section n={5} title="Is Your Information Transferred Internationally?">
          <InShort>We may transfer, store, and process your information in countries other than your own.</InShort>
          <Para>
            Our servers and certain of our service providers are located in the United States.
            Regardless of your location, your information may be transferred to, stored by, and
            processed by us and by the third parties with whom we share it, including in the United
            States and other countries.
          </Para>
          <Para>
            If you are a resident in the EEA, the UK, or Switzerland, these countries may not have data
            protection laws as comprehensive as those in your country. Where we transfer your personal
            information out of these regions, we rely on appropriate safeguards recognized under
            applicable law (such as the European Commission's Standard Contractual Clauses).
          </Para>
        </Section>

        <Section n={6} title="How Long Do We Keep Your Information?">
          <InShort>
            We keep your information for as long as necessary to fulfill the purposes outlined in this
            Privacy Notice unless otherwise required by law.
          </InShort>
          <Para>
            No purpose in this notice will require us keeping your personal information for longer than
            the period during which you have an account with us. When we have no ongoing legitimate
            business need to process your personal information, we will either delete or anonymize it,
            or, if this is not possible (for example, because it has been stored in backup archives),
            we will securely store it and isolate it from any further processing until deletion is
            possible.
          </Para>
        </Section>

        <Section n={7} title="How Do We Keep Your Information Safe?">
          <InShort>
            We aim to protect your personal information through a system of organizational and
            technical security measures.
          </InShort>
          <Para>
            We have implemented appropriate and reasonable technical and organizational security
            measures. However, despite our safeguards, no electronic transmission over the internet or
            information storage technology can be guaranteed to be 100% secure, so we cannot promise or
            guarantee that unauthorized third parties will not be able to defeat our security.
            Transmission of personal information to and from our Services is at your own risk. You
            should only access the Services within a secure environment.
          </Para>
        </Section>

        <Section n={8} title="Do We Collect Information From Minors?">
          <InShort>
            We do not knowingly collect data from or market to children under 18 years of age or the
            equivalent age as specified by law in your jurisdiction.
          </InShort>
          <Para>
            By using the Services, you represent that you are at least 18 (or the equivalent age
            specified by law in your jurisdiction), or that you are the parent or guardian of such a
            minor and consent to that minor's use of the Services. If we learn that personal
            information from users under 18 has been collected, we will deactivate the account and take
            reasonable measures to promptly delete such data. If you become aware of any data we may
            have collected from children under age 18, please contact us at{" "}
            <A href="mailto:hello@prooftous.app">hello@prooftous.app</A>.
          </Para>
        </Section>

        <Section n={9} title="What Are Your Privacy Rights?">
          <InShort>
            Depending on your region, you may have rights that allow you greater access to and control
            over your personal information.
          </InShort>
          <Para>
            In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under
            applicable data protection laws. These may include the right to request access and obtain a
            copy of your personal information, to request rectification or erasure, to restrict
            processing, to data portability, and not to be subject to automated decision-making. You
            can make such a request by contacting us using the details in section 13. If you are
            located in the EEA or UK and believe we are unlawfully processing your personal information,
            you also have the right to complain to your data protection authority. In Switzerland, you
            may contact the Federal Data Protection and Information Commissioner.
          </Para>
          <Sub>Withdrawing your consent</Sub>
          <Para>
            If we are relying on your consent, you have the right to withdraw it at any time by
            contacting us or by updating your preferences. This will not affect the lawfulness of
            processing before its withdrawal.
          </Para>
          <Sub>Account information</Sub>
          <Para>
            If you would at any time like to review or change the information in your account or
            terminate your account, you can log in to your account settings and update your user
            account, or contact us using the information provided. Upon your request to terminate your
            account, we will deactivate or delete your account and information from our active
            databases. We may retain some information to prevent fraud, troubleshoot problems, assist
            with investigations, enforce our legal terms, and/or comply with applicable legal
            requirements.
          </Para>
        </Section>

        <Section n={10} title="Controls For Do-Not-Track Features">
          <Para>
            Most web browsers and some mobile operating systems and applications include a
            Do-Not-Track ("DNT") feature you can activate to signal your privacy preference not to
            have data about your online browsing activities monitored and collected. As no uniform
            technology standard for recognizing and implementing DNT signals has been finalized, we do
            not currently respond to DNT browser signals. California law requires us to let you know
            how we respond to web browser DNT signals; because there is not yet an industry or legal
            standard for recognizing or honoring them, we do not respond to them at this time.
          </Para>
        </Section>

        <Section n={11} title="US Residents' Specific Privacy Rights">
          <InShort>
            If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana,
            Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon,
            Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have specific rights regarding
            your personal information.
          </InShort>
          <Sub>Categories of personal information we collect</Sub>
          <Para>
            The table below shows the categories of personal information we have collected in the past
            twelve (12) months.
          </Para>
          <div className="mt-5 overflow-x-auto border border-[#1F1F1F]">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-[#1F1F1F] bg-[#0A0A0A]">
                  <th className="p-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[#C9A94B]">#</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[#C9A94B]">Category</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[#C9A94B]">Examples</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[#C9A94B]">Collected</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORY_ROWS.map((r) => (
                  <tr key={r[0]} className="border-b border-[#1F1F1F] last:border-0 align-top">
                    <td className="p-3 font-mono text-[#8A8A8A]">{r[0]}</td>
                    <td className="p-3 text-white">{r[1]}</td>
                    <td className="p-3 text-[#A1A1AA]">{r[2]}</td>
                    <td className={`p-3 font-mono font-bold ${r[3] === "YES" ? "text-[#658247]" : "text-[#8A8A8A]"}`}>{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Para>
            We only collect sensitive personal information for the purposes allowed by law or with your
            consent, and we do not collect or process it for the purpose of inferring characteristics
            about you. We have not sold or shared any personal information to third parties for a
            business or commercial purpose in the preceding twelve (12) months. We retain categories A,
            D, H, and L for as long as you have an account with us.
          </Para>
          <Sub>Your rights</Sub>
          <Bullets
            items={[
              "Right to know whether or not we are processing your personal data",
              "Right to access your personal data",
              "Right to correct inaccuracies in your personal data",
              "Right to request the deletion of your personal data",
              "Right to obtain a copy of the personal data you previously shared with us",
              "Right to non-discrimination for exercising your rights",
              "Right to opt out of processing for targeted advertising, the sale of personal data, or profiling with legal or similarly significant effects",
            ]}
          />
          <Sub>How to exercise your rights</Sub>
          <Para>
            To exercise these rights, you can contact us by emailing{" "}
            <A href="mailto:hello@prooftous.app">hello@prooftous.app</A>. Under certain US state laws,
            you can designate an authorized agent to make a request on your behalf. Upon receiving your
            request, we will need to verify your identity. If we decline to take action, you may appeal
            by emailing us; if your appeal is denied, you may submit a complaint to your state attorney
            general.
          </Para>
        </Section>

        <Section n={12} title="Do We Make Updates To This Notice?">
          <InShort>Yes, we will update this notice as necessary to stay compliant with relevant laws.</InShort>
          <Para>
            The updated version will be indicated by an updated "Last updated" date at the top of this
            Privacy Notice. If we make material changes, we may notify you either by prominently posting
            a notice or by directly sending you a notification. We encourage you to review this Privacy
            Notice frequently.
          </Para>
        </Section>

        <Section n={13} title="How Can You Contact Us?">
          <Para>
            If you have questions or comments about this notice, you may contact us by email at{" "}
            <A href="mailto:hello@prooftous.app">hello@prooftous.app</A>.
          </Para>
          <div className="mt-5 border border-[#1F1F1F] bg-[#0A0A0A] p-5 font-mono text-sm">
            <div className="text-white">Proof It</div>
            <div className="mt-1 text-[#C9A94B]">hello@prooftous.app</div>
          </div>
        </Section>

        <Section n={14} title="Review, Update, Or Delete Your Data">
          <Para>
            Based on the applicable laws of your country or state of residence, you may have the right
            to request access to the personal information we collect from you, details about how we
            have processed it, and the ability to correct inaccuracies or delete your personal
            information. You may also have the right to withdraw your consent to our processing. To
            request to review, update, or delete your personal information, please email us at{" "}
            <A href="mailto:hello@prooftous.app">hello@prooftous.app</A> or use your in-app account
            settings.
          </Para>
        </Section>

        <div className="mt-16 border-t border-[#1F1F1F] pt-8">
          <Link
            to="/"
            data-testid="privacy-back-home-bottom"
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#C9A94B] transition-colors hover:text-[#DCC271]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={2} />
            Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}
